import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";

import { ApiError } from "../../api/client";
import { THREAT_TO_LABELS, type ThreatTo } from "../../wizard/ThreatStep";
import { ReportsApi, type ThreatItem } from "./api";

// Backend (routes/reports.py) emits five kinds. Keep this map exhaustive —
// a missing entry crashes the whole list with `KIND_HREF[i.kind] is not a
// function`. New kinds added server-side MUST be mirrored here.
const KIND_LABEL: Record<ThreatItem["kind"], string> = {
  case: "Case",
  intel: "Intel",
  poi: "POI",
  performance: "Performance",
  popularity: "Popularity",
};

const KIND_HREF: Record<ThreatItem["kind"], (id: string) => string> = {
  case: (id) => `/app/cases/${id}`,
  intel: () => `/app/intel`,
  poi: (id) => `/app/poi/${id}`,
  performance: () => `/app/performance`,
  popularity: () => `/app/popularity`,
};

const KIND_COLOR: Record<ThreatItem["kind"], string> = {
  case: "#2563eb",
  intel: "#dc2626",
  poi: "#7c3aed",
  performance: "#0891b2",
  popularity: "#db2777",
};

/**
 * Threat Detection — every record (case, intel, POI) tagged with a non-zero
 * threat against the party / a leader / an area. Records explicitly marked
 * "Not a threat" do not show here.
 */
export function Threats() {
  const data = useQuery({ queryKey: ["report.threats"], queryFn: ReportsApi.threats });
  const [filterTarget, setFilterTarget] = useState<ThreatTo | "all">("all");
  const [filterKind, setFilterKind] = useState<ThreatItem["kind"] | "all">("all");
  // Surface the raw response in the dev console so a "blank page" report can
  // be debugged from a browser tab without server-side access. ApiError carries
  // the structured 5xx detail (errorClass / location / traceback) the API now
  // sends — log it as an object so it stays browseable rather than collapsing
  // to "[object Object]".
  useEffect(() => {
    if (data.data) console.log("[threats] response", data.data);
    if (data.error) {
      const e = data.error;
      if (e instanceof ApiError) {
        console.error("[threats] api error", {
          status: e.status,
          code: e.code,
          message: e.message,
          requestId: e.requestId,
          errorClass: e.errorClass,
          errorMessage: e.errorMessage,
          errorLocation: e.errorLocation,
          traceback: e.tracebackLines,
        });
      } else {
        console.error("[threats] error", e);
      }
    }
  }, [data.data, data.error]);

  const items = (data.data?.items ?? [])
    .filter((i) => filterTarget === "all" || i.threatTo === filterTarget)
    .filter((i) => filterKind === "all" || i.kind === filterKind);
  const totalRaw = data.data?.items.length ?? 0;

  return (
    <div>
      <header style={{ marginBottom: 12 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Threat Detection</h1>
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>
          Every record flagged as a threat. Cases, intel and POI items where capture-time tagging
          marked them as a threat against the party, a leader, or an area appear here. Use this
          board to escalate — items without a threat tag are not listed.
        </p>
      </header>

      {data.isLoading && <p>Loading…</p>}
      {data.error && <ThreatsErrorPanel err={data.error} />}

      {!data.isLoading && !data.error && (
        <>
          <div className="stat-row" style={{ marginBottom: 16 }}>
            {(Object.keys(THREAT_TO_LABELS) as ThreatTo[])
              .filter((t) => t !== "none")
              .map((t) => (
                <div key={t} className="stat" style={{ cursor: "pointer", borderColor: filterTarget === t ? "var(--accent)" : "var(--border)" }} onClick={() => setFilterTarget(filterTarget === t ? "all" : t)}>
                  <div className="stat-value">{data.data?.byTarget[t] ?? 0}</div>
                  <div className="stat-label">{THREAT_TO_LABELS[t]}</div>
                </div>
              ))}
          </div>

          {totalRaw === 0 && (
            <div className="muted" style={{ padding: 16, border: "1px dashed var(--border)", borderRadius: 6, marginBottom: 16 }}>
              No threat-tagged records were returned. This usually means one of: (a) no records have been
              tagged with a threat target yet, (b) the records that exist sit outside your assigned scope,
              or (c) you don't have <code>report.generate</code>. Open the browser dev console to inspect
              the raw <code>/reports/threats</code> response.
            </div>
          )}

          <div className="row-3" style={{ marginBottom: 16 }}>
            <label>
              Threat to:
              <select value={filterTarget} onChange={(e) => setFilterTarget(e.target.value as ThreatTo | "all")}>
                <option value="all">All</option>
                {(Object.keys(THREAT_TO_LABELS) as ThreatTo[])
                  .filter((t) => t !== "none")
                  .map((t) => <option key={t} value={t}>{THREAT_TO_LABELS[t]}</option>)}
              </select>
            </label>
            <label>
              Source:
              <select value={filterKind} onChange={(e) => setFilterKind(e.target.value as ThreatItem["kind"] | "all")}>
                <option value="all">All</option>
                <option value="case">Cases</option>
                <option value="intel">Intel</option>
                <option value="poi">POI</option>
                <option value="performance">Performance</option>
                <option value="popularity">Popularity</option>
              </select>
            </label>
            <span />
          </div>

          <div className="card-grid">
            {items.map((i) => {
              // Defensive lookups: if the backend ever introduces a new kind
              // before the client knows about it, render a generic card
              // pointing at /app rather than crashing the list.
              const hrefBuilder = KIND_HREF[i.kind] ?? (() => "/app");
              const label = KIND_LABEL[i.kind] ?? String(i.kind);
              const color = KIND_COLOR[i.kind] ?? "#6b7280";
              return (
              <Link key={`${i.kind}-${i.id}`} to={hrefBuilder(i.id)} className="list-card">
                <div className="list-card-header">
                  <span style={{
                    fontSize: 11, padding: "2px 8px", borderRadius: 999,
                    background: color + "1a", color, fontWeight: 600,
                  }}>{label}</span>
                  <span style={{
                    fontSize: 11, padding: "2px 8px", borderRadius: 999,
                    background: "var(--danger-soft, #fee2e2)", color: "var(--danger)", fontWeight: 600,
                  }}>{THREAT_TO_LABELS[i.threatTo]}</span>
                </div>
                <div className="list-card-title">{i.title}</div>
                <div className="list-card-snippet">
                  {i.threatTarget ? <strong>{i.threatTarget}</strong> : null}
                  {i.threatTarget && i.threatDetails ? <> — </> : null}
                  {i.threatDetails || (!i.threatTarget && "No details captured.")}
                </div>
                <div className="list-card-foot">
                  <span>{i.classification ?? "—"}</span>
                  <span>{i.createdAt ? new Date(i.createdAt).toLocaleDateString() : "—"}</span>
                </div>
              </Link>
              );
            })}
            {items.length === 0 && !data.isLoading && (
              <div className="muted" style={{ padding: 16, gridColumn: "1 / -1" }}>
                No threats match your filters. Either the system is quiet, or no records have been
                tagged with a threat target yet.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Render the full structured detail of an API failure on the Threats screen.
 * Pulls every diagnostic field (status, code, request-id, exception class +
 * location, traceback) out of ApiError so a 500 doesn't blank the page —
 * you get the cause inline. Mirrors the LoadFailure card layout.
 */
function ThreatsErrorPanel({ err }: { err: unknown }) {
  if (err instanceof ApiError) {
    return (
      <div className="card" style={{ marginBottom: 16, borderColor: "var(--danger)" }}>
        <div className="card-title" style={{ color: "var(--danger)" }}>Failed to load threats</div>
        <div className="card-sub" style={{ marginBottom: 8 }}>
          <code>HTTP {err.status}</code>
          {err.code && <code style={{ marginLeft: 6 }}>{err.code}</code>}
          {err.requestId && <code style={{ marginLeft: 6, color: "var(--muted)" }}>req {err.requestId}</code>}
        </div>
        <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{err.message}</div>
        {(err.errorClass || err.errorLocation) && (
          <div style={{ marginTop: 10, padding: 8, background: "var(--bg-muted, #fff5f5)", borderRadius: 4, fontSize: 12 }}>
            {err.errorClass && <div><strong>Exception:</strong> <code>{err.errorClass}</code></div>}
            {err.errorMessage && err.errorMessage !== err.message && (
              <div><strong>Message:</strong> <code>{err.errorMessage}</code></div>
            )}
            {err.errorLocation && <div><strong>Where:</strong> <code>{err.errorLocation}</code></div>}
          </div>
        )}
        {err.tracebackLines && err.tracebackLines.length > 0 && (
          <details style={{ marginTop: 8 }}>
            <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--muted)" }}>traceback</summary>
            <pre style={{ fontSize: 11, marginTop: 6, color: "var(--muted)", whiteSpace: "pre-wrap" }}>
              {err.tracebackLines.join("")}
            </pre>
          </details>
        )}
        <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
          Open the browser dev console — the same payload is logged with prefix <code>[threats]</code> for copy-paste.
        </div>
      </div>
    );
  }
  return (
    <div className="error" style={{ padding: 12 }}>
      <strong>Failed to load threats.</strong>{" "}
      {err instanceof Error ? err.message : String(err)}
    </div>
  );
}
